import React from 'react';
import { Box } from '@mui/material';
import FieldRow from './FieldRow';
import LocalizedField from './LocalizedField';
import SectionBlock from './SectionBlock';
import { fmtLocalDate, formatDateRanges, summarizeDates } from '../../../utils/dateUtils';

export default function CampaignDetailPanel({ data, diffKeys = new Set(), side }) {
  if (!data) return null;

  // Present only on a proposal that moves the dates and cleans up the campaign's ads.
  const ads       = Array.isArray(data.adCleanup?.ads) ? data.adCleanup.ads : [];
  const deleteAll = data.adCleanup?.mode === 'delete_all';

  // Support both { id } and { campaignId } shapes
  const nameData = data.localizedName ?? data.localizedPairs;

  return (
    <Box>
      <SectionBlock title="Identity" />
      <LocalizedField
        label="Name"
        fieldKey={data.localizedName ? 'localizedName' : 'localizedPairs'}
        data={nameData}
        diffKeys={diffKeys}
        side={side}
      />
      <FieldRow
        label="Status"
        value={data.status}
        isChanged={diffKeys.has('status')}
        side={side}
      />

      <SectionBlock title="Schedule" />
      <FieldRow
        label="Start Date"
        value={fmtLocalDate(data.startDate)}
        isChanged={diffKeys.has('startDate')}
        side={side}
      />
      <FieldRow
        label="End Date"
        value={fmtLocalDate(data.endDate)}
        isChanged={diffKeys.has('endDate')}
        side={side}
      />

      <SectionBlock title="Pricing" />
      <FieldRow
        label="Daily Rate"
        value={data.dailyRate != null ? String(data.dailyRate) : null}
        isChanged={diffKeys.has('dailyRate')}
        side={side}
      />
      <FieldRow
        label="Duration Modifier"
        value={data.durationModifier != null ? String(data.durationModifier) : null}
        isChanged={diffKeys.has('durationModifier')}
        side={side}
      />
      <FieldRow
        label="Cost / Day"
        value={data.costPerDay != null ? String(data.costPerDay) : null}
        isChanged={diffKeys.has('costPerDay')}
        side={side}
      />
      <FieldRow
        label="Total Cost"
        value={data.totalCost != null ? String(data.totalCost) : null}
        isChanged={diffKeys.has('totalCost')}
        side={side}
      />

      {ads.length > 0 && (
        <>
          <SectionBlock title="Ads affected by the date change" />
          <FieldRow
            label="What happens"
            value={deleteAll
              ? 'All ads in this campaign are deleted'
              : 'Ads are kept; run dates outside the new campaign dates are removed'}
            isChanged
            side={side}
          />
          {ads.map((ad) => {
            const removed = ad.removedDates ?? [];
            const kept    = ad.keptDates ?? [];
            const history = ad.historyDates ?? [];   // before today: kept as run history, never changed
            const note    = history.length ? `; ${history.length} past date${history.length === 1 ? '' : 's'} kept as history (${formatDateRanges(history, 3)})` : '';
            return (
              <FieldRow
                key={ad.adId}
                label={ad.name || `Ad #${ad.adId}`}
                value={ad.willBeDeleted
                  ? `Will be deleted (had ${removed.length + kept.length} upcoming run date${removed.length + kept.length === 1 ? '' : 's'})${note}`
                  : `${kept.length} run date${kept.length === 1 ? '' : 's'} kept; removed: ${summarizeDates(removed) || 'none'}${note}`}
                isChanged
                side={side}
              />
            );
          })}
        </>
      )}
    </Box>
  );
}
